/* eslint-disable spellcheck/spell-checker */
import React, { useState, useMemo, useEffect } from 'react';

import { rawCacaClient } from '@plangrid-private/web-api-clients';
import { v4 as uuidv4 } from 'uuid';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import Divider from '@adsk/alloy-react-divider';
import { MagnifyingGlassIcon, PlusIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { DropdownButton, MenuCategory, MenuList } from '@adsk/alloy-react-menu';
import Overlay from '@adsk/alloy-react-overlay';
import TextInput from '@adsk/alloy-react-text-input';

import { useFormsContext } from './FormsProvider';
import FormsTemplateItem from './FormsTemplateItem';
import { FEATURE_FLAGS } from './helpers/constants';
import { formsClient } from './helpers/formsClient';
import { ACTIONS } from './helpers/reducers/forms';
import createFormTransformers from './helpers/transformers/createForm';
import {
  getCreateFormUrl,
  getDateUTC,
  getFormCollaborationMode,
} from './helpers/utils';
import {
  useFormTemplatesByUidMap,
  useUserPermissionInfo,
  useCanUserCreateForm,
} from './hooks';
import { usePlangridFeatureFlag } from './PlangridLaunchDarklyProvider';

const DropdownContent = React.forwardRef(
  (
    { style, options, onClose, searchQuery, handleQueryChange, createForm },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return (
      <Overlay.Container
        ref={ref}
        style={style}
        variant={Overlay.VARIANTS.LIGHT}
      >
        <Flex flexDirection="column">
          <Flex alignItems="center" padding="15px 15px 10px 15px">
            <TextInput
              autoFocus
              value={searchQuery}
              type={TextInput.TYPES.TEXT}
              renderLeftElement={(p) => <MagnifyingGlassIcon {...p} />}
              placeholder={T.translate('SEARCH_TEMPLATE')}
              onChange={handleQueryChange}
            />
          </Flex>
          <Divider />
          <Box width={style.width} height="250px" overflowY="scroll">
            {options.map((category, index) => (
              <MenuCategory key={index} title={category.title}>
                <MenuList>
                  {category.itemOptions.map((item) => (
                    <FormsTemplateItem
                      key={item.uid}
                      template={item}
                      onClick={createForm}
                      onClose={onClose}
                    />
                  ))}
                </MenuList>
              </MenuCategory>
            ))}
          </Box>
        </Flex>
      </Overlay.Container>
    );
  },
);

const FormsCustomCreateNewButton = ({
  onCreateClick,
  disabled,
  createFormProps,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

  const { value: isScalingFormsM7Enabled } = usePlangridFeatureFlag(
    FEATURE_FLAGS.SCALING_FORMS_M7,
    false,
  );
  const { value: isInheritLocationFromAssetEnabled } = usePlangridFeatureFlag(
    FEATURE_FLAGS.INHERIT_LOCATION_FROM_ASSET,
    false,
  );
  const isAssetInURL = window.location.pathname.includes('asset');
  // Restrict the location id inheritance to Assets
  const locationId =
    isInheritLocationFromAssetEnabled && isAssetInURL
      ? createFormProps?.sourceMetadata?.locationId
      : undefined;

  const {
    formTemplates,
    forgeUserMapping: forgeUserMappingByPGID,
    pgProjectUser,
    isLoadingPgProjectUser,
    isCreateRequestInFlight,
    templateCategories,
    dispatch,
  } = useFormsContext();
  const forgeUserMappingByOxygenID = useMemo(() => {
    const forgeMappingByOxygenID = {};
    for (const [PG, Oxygen] of Object.entries(forgeUserMappingByPGID)) {
      forgeMappingByOxygenID[Oxygen] = PG;
    }

    return forgeMappingByOxygenID;
  }, [forgeUserMappingByPGID]);
  const { data: userPermissionInfo, loading: isLoadingPermissionInfo } =
    useUserPermissionInfo(pgProjectUser?.user_id || '');
  const { data: formTemplatesMap, loading: isLoadingFormTemplatesMap } =
    useFormTemplatesByUidMap();
  const projectId = useCurrentProjectId();
  const canUserCreateForm = useCanUserCreateForm();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingVisibleTemplates, setIsLoadingVisibleTemplates] =
    useState(true);
  const [filteredTemplates, setFilteredTemplates] = useState([]);

  const templatesWithCategories = useMemo(() => {
    if (
      isLoadingFormTemplatesMap ||
      isLoadingPgProjectUser ||
      isLoadingPermissionInfo
    )
      return [];

    const uidsInCategories = new Set();

    const visibleTemplates = formTemplates.filter((template) =>
      canUserCreateForm(userPermissionInfo, template),
    );

    const visibleTemplatesWithCategories = templateCategories.map(
      ({ name, templates }, index) => {
        const visibleCategoryTemplates = templates.reduce(
          (acc, templateUid) => {
            const currTemplate = formTemplatesMap[templateUid];
            if (canUserCreateForm(userPermissionInfo, currTemplate)) {
              uidsInCategories.add(templateUid);
              return [...acc, currTemplate];
            }

            return acc;
          },
          [],
        );

        return {
          key: `${name}-${index}`,
          title: `${T.translate('ADDED_TO')} ${name}`,
          showSelection: false,
          itemOptions: visibleCategoryTemplates,
        };
      },
    );

    visibleTemplatesWithCategories.push({
      key: 'ALL_TEMPLATES',
      title: T.translate('ALL_TEMPLATES'),
      showSelection: false,
      itemOptions: visibleTemplates.filter(
        ({ uid }) => !uidsInCategories.has(uid),
      ),
    });

    setIsLoadingVisibleTemplates(false);
    return visibleTemplatesWithCategories;
  }, [
    formTemplates,
    formTemplatesMap,
    isLoadingFormTemplatesMap,
    isLoadingPgProjectUser,
    isLoadingPermissionInfo,
    templateCategories,
    userPermissionInfo,
    canUserCreateForm,
    T,
  ]);

  const handleQueryChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const createForm = async (template) => {
    dispatch({
      type: ACTIONS.CREATE_FORM_START,
    });

    const postBody = {
      report_date: getDateUTC(new Date()),
      description: '',
      location_id: locationId,
    };

    if (template.pg_form) {
      const pgForm = template.pg_form;
      const pgFormUid = uuidv4();

      const pgFormLayout = {
        ...pgForm.file.layout,
        template_uid: pgForm.file.form_uid,
      };

      const pgFormFile = {
        ...pgForm.file,
        layout: pgFormLayout,
        form_uid: pgFormUid,
      };

      postBody.pg_form = {
        uid: pgFormUid,
        file: pgFormFile,
      };
    }

    try {
      let form = {};
      if (isScalingFormsM7Enabled) {
        const apsForm = await formsClient.createForm(
          projectId,
          template.uid,
          postBody,
        );

        form = createFormTransformers.translateResponse(
          projectId,
          apsForm,
          forgeUserMappingByOxygenID,
        );
      } else {
        // TODO: FORMS-6571 Add route to @plangrid-private/web-api-clients
        form = await rawCacaClient.post(
          getCreateFormUrl(projectId, template.uid, uuidv4()),
          postBody,
        );
      }

      dispatch({
        type: ACTIONS.CREATE_FORM_COMPLETE,
        payload: {
          newForm: {
            ...form,
            id: form.uid,
          },
          newFormTemplate: {
            ...template,
            formCollaborationMode: getFormCollaborationMode(template),
          },
        },
      });

      onCreateClick();
    } catch (e) {
      dispatch({ type: ACTIONS.CREATE_FORM_ERROR });
    }
  };

  useEffect(() => {
    const filtered = templatesWithCategories.reduce((acc, category) => {
      const filteredItemOptions = category.itemOptions.filter(({ name }) =>
        name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      if (filteredItemOptions.length > 0) {
        return [
          ...acc,
          {
            ...category,
            itemOptions: category.itemOptions.filter(({ name }) =>
              name.toLowerCase().includes(searchQuery.toLowerCase()),
            ),
          },
        ];
      }

      return acc;
    }, []);
    setFilteredTemplates(filtered);
  }, [templatesWithCategories, searchQuery]);

  return (
    <DropdownButton
      options={filteredTemplates}
      loading={
        isLoadingFormTemplatesMap ||
        isCreateRequestInFlight ||
        isLoadingVisibleTemplates
      }
      size={DropdownButton.SIZES.MEDIUM}
      placement={DropdownButton.PLACEMENTS.BOTTOM_START}
      disabled={disabled}
      data-testid="CreateFormDropdownButton"
      menuStyle={{
        width: '290px',
        height: '310px',
        overflow: 'hidden',
      }}
      renderRightElement={(p) => <PlusIcon {...p} />}
      renderRightIcon={() => null}
      renderContent={(p) => (
        <DropdownContent
          {...p}
          searchQuery={searchQuery}
          handleQueryChange={handleQueryChange}
          createForm={createForm}
        />
      )}
    >
      {T.translate('CREATE_FORM')}
    </DropdownButton>
  );
};
export default FormsCustomCreateNewButton;
